import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Day } from '@e3dc-react/shell/Libs/date/types'
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import { TrashIcon } from 'src/Libs/flow/icons'
import { InstallationFormModel, WeekDay } from 'src/Libs/flow/installation/forms'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { isDateXBeforeDateY } from 'src/Libs/flow/installation/utils'
import { transformDateInput } from 'src/Libs/flow/utils'
import { TimePicker } from 'src/Libs/inputs/react-hook-form'
import WeekDaySelector from 'src/Elements/Selectors/WeekDaySelector'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

type BlockingTimeType = keyof Pick<
  InstallationFormModel['storageSystem']['generalSettings']['advancedSettings'],
  'chargeBlockingWeeklySchedule' | 'dischargeBlockingWeeklySchedule'
>

const shortToFullConfig: Record<Day, WeekDay> = {
  mo: 'monday',
  tu: 'tuesday',
  we: 'wednesday',
  th: 'thursday',
  fr: 'friday',
  sa: 'saturday',
  su: 'sunday',
}

const fullToShortConfig: Record<WeekDay, Day> = {
  monday: 'mo',
  tuesday: 'tu',
  wednesday: 'we',
  thursday: 'th',
  friday: 'fr',
  saturday: 'sa',
  sunday: 'su',
}

export const BlockingTimesSchedule: React.FC<{
  blockingTimeType: BlockingTimeType
  index: number
  remove: () => void
  conflictingScheduleIndex: number | null | undefined
}> = ({ blockingTimeType, index, remove, conflictingScheduleIndex }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const { control, setError, clearErrors, setValue } = useInstallationForm()

  const schedule = useWatch({
    control,
    name: `storageSystem.generalSettings.advancedSettings.${blockingTimeType}.${index}`,
  })

  const hasNoSelectedDays = Object.values(schedule.weekDays).every(value => value === false)
  const toIsBeforeFrom = isDateXBeforeDateY(schedule.toTime, schedule.fromTime)
  const hasScheduleConflict = conflictingScheduleIndex !== null && conflictingScheduleIndex !== undefined

  const hasError = hasNoSelectedDays || toIsBeforeFrom || hasScheduleConflict

  useEffect(() => {
    if (hasError) {
      setError(`storageSystem.generalSettings.advancedSettings.${blockingTimeType}.${index}`, {
        type: 'schedule-error',
      })
    } else {
      clearErrors(`storageSystem.generalSettings.advancedSettings.${blockingTimeType}.${index}`)
    }
  }, [hasError, setError, clearErrors, index, blockingTimeType])

  const isSecondaryColor = blockingTimeType === 'dischargeBlockingWeeklySchedule'
  const days = Object.fromEntries(
    Object.entries(schedule.weekDays).map(([day, value]) => [fullToShortConfig[day as WeekDay], value as boolean])
  ) as Record<Day, boolean>

  return (
    <Box sx={{ my: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          '& > div:first-of-type': { flex: 1, position: 'relative' },
        }}
      >
        <SettingsBox
          title={t('storageSystem.extendedSettings.blockingTime')}
          suffix={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  paddingInline: {
                    xs: 1,
                    sm: 0,
                  },

                  [theme.breakpoints.down('sm')]: {
                    flexDirection: 'column',
                  },
                }}
              >
                <TimePicker
                  name={`storageSystem.generalSettings.advancedSettings.${blockingTimeType}.${index}.fromTime`}
                  label={t('common.from')}
                  onTransform={transformDateInput}
                  disabled={!canEdit}
                />
                <TimePicker
                  name={`storageSystem.generalSettings.advancedSettings.${blockingTimeType}.${index}.toTime`}
                  label={t('common.to')}
                  onTransform={transformDateInput}
                  disabled={!canEdit}
                />

                {canEdit && downSm && (
                  <IconButton color="default" onClick={remove} sx={{ position: 'absolute', right: 6, top: 6 }}>
                    <TrashIcon />
                  </IconButton>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: {
                    sm: 'center',
                    md: 'flex-end',
                  },
                }}
              >
                <WeekDaySelector
                  days={days}
                  onSelectDay={(day, isSelected) => {
                    setValue(
                      `storageSystem.generalSettings.advancedSettings.${blockingTimeType}.${index}.weekDays.${shortToFullConfig[day]}`,
                      isSelected,
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      }
                    )
                  }}
                  disabled={!canEdit}
                  buttonColor={isSecondaryColor ? theme.palette.secondary.main : undefined}
                />
              </Box>
            </Box>
          }
          depth={1}
        />
        {canEdit && !downSm && (
          <IconButton color="default" onClick={remove} sx={{ fontSize: 14, maxHeight: 'auto', maxWidth: 'auto' }}>
            {t('common.delete')}
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', color: 'error.main' }}>
        {toIsBeforeFrom && <ErrorMessage title={t('user-settings.boiler-page.calendar-editor.time-order-error')} />}
        {hasNoSelectedDays && <ErrorMessage title={t('installation.tariff.missingDay')} />}
        {hasScheduleConflict && (
          <ErrorMessage
            title={t('user-settings.boiler-page.calendar-editor.schedules-overlap-error', {
              value: conflictingScheduleIndex + 1,
            })}
          />
        )}
      </Box>
    </Box>
  )
}
